/**
 * Listens for the updateGpcPreferences custom event dispatched when the session
 * resolves and relays the browser's Global Privacy Control signal back to DXL
 * via the SDK module's updateGpcAction mutation.
 *
 * @module hooks/useUpdateGpcPreferences
 * @memberof -Common
 */
import { useEffect } from 'react';

import constants from '@ulta/core/utils/constants/constants';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import * as utils from './useUpdateGpcPreferences';

export const useUpdateGpcPreferences = ( data ) => {
  const { invokeMutation } = handleEmptyObjects( data );

  useEffect( () => {
    if( !invokeMutation ){
      return;
    }

    const handleUpdateGpcPreferences = ( e ) => {
      const { config = {} } = e.detail || {};
      utils.processUpdateGpcPreferences(
        { config },
        { invokeMutation }
      );
    };

    document.addEventListener( constants.EVENTS.UPDATE_GPC_PREFERENCES, handleUpdateGpcPreferences );

    return () => {
      document.removeEventListener( constants.EVENTS.UPDATE_GPC_PREFERENCES, handleUpdateGpcPreferences );
    };
  }, [invokeMutation] );
};

/**
 * Merges the GPC config into the action's moduleParams and invokes the mutation
 *
 * @param {object} data - Object containing action and config
 * @param {object} methods - Object containing invokeMutation function
 */
export const processUpdateGpcPreferences = ( data, methods ) => {
  const { config } = handleEmptyObjects( data );
  const { invokeMutation } = handleEmptyObjects( methods );

  if( isServer() ){
    return;
  }

  const updateGPCAction = global.ULTA_SDK?.updateGPCAction;

  if( !updateGPCAction || !invokeMutation ){
    return;
  }

  const existingVariables = updateGPCAction.variables || {};
  const existingModuleParams = existingVariables.moduleParams || {};

  invokeMutation( {
    ...updateGPCAction,
    variables: {
      ...existingVariables,
      moduleParams: {
        ...existingModuleParams,
        ...config
      }
    }
  } );
};
